import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, fmtErr, fmtDateTime, fmtSize } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChatThread from "@/components/ChatThread";
import { Thumb } from "@/components/common";
import { StatusBadge, PriorityBadge, STATUS_LABELS } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Paperclip, FileText, Download, Mail, Lock, RotateCcw, Sparkles, Loader2, X } from "lucide-react";

const AI_PRIO_LABELS = { basse: "Basse", normale: "Normale", haute: "Haute", urgente: "Urgente" };
const AI_PROPOSAL_TITLES = { reformulation: "Réponse reformulée", resume: "Résumé du ticket", synthese: "Synthèse de clôture", suggestion: "Suggestion catégorie & priorité" };

function MetaRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-2.5 last:border-b-0 text-sm">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children || "—"}</span>
    </div>
  );
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [staff, setStaff] = useState([]);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeComment, setCloseComment] = useState("");
  const [aiStatus, setAiStatus] = useState(null);
  const [aiBusy, setAiBusy] = useState("");
  const [aiProposal, setAiProposal] = useState(null);
  const [aiApplied, setAiApplied] = useState(false);

  const reformulateInline = async () => {
    setAiBusy("reformuler-inline");
    try {
      const { data } = await api.post(`/tickets/${id}/ai/reformuler`, { text: reply });
      setReply(data.result);
      setAiApplied(true);
      setAiProposal(null);
      toast.success("Version IA prête — relisez puis cliquez sur Envoyer.");
    } catch (e) {
      toast.error(fmtErr(e, "L'Assistant IA n'est pas disponible pour le moment."));
    } finally {
      setAiBusy("");
    }
  };

  const runAi = async (action, fn) => {
    setAiBusy(action);
    setAiProposal(null);
    try {
      const { data } = await fn();
      if (action === "reformuler") setAiProposal({ type: "reformulation", text: data.result });
      else if (action === "resumer") { setAiProposal({ type: "resume", text: data.result }); load(); }
      else if (action === "synthese") setAiProposal({ type: "synthese", text: data.result });
      else if (action === "suggestion") setAiProposal({ type: "suggestion", ...data });
    } catch (e) {
      toast.error(fmtErr(e, "L'Assistant IA n'est pas disponible pour le moment."));
    } finally {
      setAiBusy("");
    }
  };

  const pickFiles = (list) => {
    let arr = Array.from(list || []);
    const tooBig = arr.filter((f) => f.size > 5 * 1024 * 1024);
    if (tooBig.length) {
      toast.error("Chaque fichier doit faire moins de 5 Mo.");
      arr = arr.filter((f) => f.size <= 5 * 1024 * 1024);
    }
    if (arr.length > 15) {
      toast.error("Vous pouvez joindre jusqu'à 15 fichiers maximum par message.");
      arr = arr.slice(0, 15);
    }
    setFiles(arr);
  };

  const closeTicket = async () => {
    try {
      await api.post(`/tickets/${id}/close`, { comment: closeComment || null });
      toast.success("Ticket clôturé — notification client enregistrée (simulation)");
      setCloseOpen(false);
      setCloseComment("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reopenTicket = async () => {
    try {
      await api.post(`/tickets/${id}/reopen`);
      toast.success("Ticket rouvert — notification client enregistrée (simulation)");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const fileInput = useRef(null);
  const isStaff = user?.role !== "client_user";
  const canAssign = ["super_admin", "company_admin", "manager"].includes(user?.role);
  const AI_ABBREV = /\b(svp|stp|pb|pblm|rdv|dsl|bcp|cad|càd|tkt|jsp|pk|pcq|mdr|slt|cc|bjr|bsr|asap|ok|nickel|prob|info|dispo|maj)\b/i;
  const wordCount = reply.trim() ? reply.trim().split(/\s+/).filter(Boolean).length : 0;
  const suggestReformulate = isStaff && aiStatus?.enabled && !internal && reply.trim().length > 0 && !aiApplied
    && (wordCount < 10 || AI_ABBREV.test(reply));
  const backPath = isStaff ? "/app/tickets" : "/portal";

  const load = () => api.get(`/tickets/${id}`).then(({ data }) => setTicket(data)).catch((e) => toast.error(fmtErr(e)));
  const silentLoad = () => api.get(`/tickets/${id}`).then(({ data }) => setTicket(data)).catch(() => {});
  useEffect(() => {
    load();
    if (canAssign) api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.role !== "super_admin" && u.status === "active"))).catch(() => {});
    if (isStaff) api.get(`/tickets/${id}/ai/status`).then(({ data }) => setAiStatus(data)).catch(() => setAiStatus(null));
  }, [id]);

  useEffect(() => {
    const t = setInterval(silentLoad, 10000);
    return () => clearInterval(t);
  }, [id]);

  const [typingUsers, setTypingUsers] = useState([]);
  const lastTypingRef = useRef(0);
  useEffect(() => {
    setTypingUsers([]);
    const t = setInterval(() => {
      api.get(`/tickets/${id}/typing`).then(({ data }) => setTypingUsers(data.typing || [])).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [id]);

  const notifyTyping = () => {
    if (internal) return;
    const now = Date.now();
    if (now - lastTypingRef.current > 2500) {
      lastTypingRef.current = now;
      api.post(`/tickets/${id}/typing`).catch(() => {});
    }
  };

  const typingLabel = typingUsers.length === 1
    ? `${typingUsers[0]} est en train d'écrire…`
    : typingUsers.length > 1
      ? `${typingUsers.length} personnes sont en train d'écrire…`
      : null;


  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() && files.length === 0) return;
    setSending(true);
    try {
      let messageId = null;
      if (reply.trim()) {
        const { data } = await api.post(`/tickets/${id}/messages`, { content: reply, internal });
        messageId = data.id;
      }
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        if (messageId) fd.append("message_id", messageId);
        if (internal) fd.append("internal", "true");
        await api.post(`/tickets/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setReply("");
      setFiles([]);
      setInternal(false);
      setAiApplied(false);
      toast.success(internal ? "Note interne ajoutée (invisible côté client)" : "Message envoyé — notification enregistrée (simulation)");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      await api.patch(`/tickets/${id}/status`, { status });
      toast.success(status === "cloture" ? "Ticket clôturé — email de clôture enregistré (simulation)" : "Statut mis à jour");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const changeAssignee = async (assignee_id) => {
    try {
      await api.patch(`/tickets/${id}/assign`, { assignee_id: assignee_id === "none" ? null : assignee_id });
      toast.success("Assignation mise à jour");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!ticket) return <div className="p-8 text-sm text-muted-foreground">Chargement…</div>;
  const images = ticket.files?.filter((f) => f.mime?.startsWith("image/")) || [];
  const docs = ticket.files?.filter((f) => !f.mime?.startsWith("image/")) || [];

  return (
    <div data-testid="ticket-detail-page">
      <Link to={backPath} data-testid="back-to-tickets" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Tickets
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm font-semibold text-primary" data-testid="ticket-number">{ticket.number}</span>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="ticket-subject">{ticket.subject}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {ticket.company_name} · {ticket.client_name} · créé le {fmtDateTime(ticket.created_at)}
          </p>
        </div>
        {isStaff && (
          <div className="flex flex-wrap items-center gap-2">
            {ticket.status !== "cloture" ? (
              <Button data-testid="close-ticket-button" variant="outline" onClick={() => setCloseOpen(true)}
                className="rounded-sm border-primary text-primary hover:bg-primary hover:text-white">
                <Lock className="mr-2 h-4 w-4" /> Clôturer le ticket
              </Button>
            ) : (
              <Button data-testid="reopen-ticket-button" variant="outline" onClick={reopenTicket} className="rounded-sm">
                <RotateCcw className="mr-2 h-4 w-4" /> Rouvrir le ticket
              </Button>
            )}
            <Select value={ticket.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="ticket-status-select" className="w-52 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            {canAssign && (
              <Select value={ticket.assignee_id || "none"} onValueChange={changeAssignee}>
                <SelectTrigger data-testid="ticket-assignee-select" className="w-48 rounded-sm"><SelectValue placeholder="Non assigné" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </div>

      {ticket.status === "cloture" && (
        <div data-testid="closed-banner" className="mb-6 rounded-sm border border-green-300 bg-green-50 p-4 text-sm dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <b>Ticket clôturé</b> le {fmtDateTime(ticket.closed_at)}{ticket.closed_by_name ? ` par ${ticket.closed_by_name}` : ""}.
          {isStaff ? " Vous pouvez le rouvrir si nécessaire." : " Ce ticket est clôturé, vous ne pouvez plus y répondre."}
        </div>
      )}

      <div className="grid min-w-0 gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2 space-y-6">
          <div className="rounded-sm border border-border bg-card p-5">
            <h2 className="mb-5 text-lg font-medium">Fil de discussion</h2>
            <ChatThread messages={ticket.messages} files={ticket.files || []}
              currentUserId={user?.id} ticketId={id} onEdited={load} />
            {typingLabel && (
              <div data-testid="typing-indicator" className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/60" />
                </span>
                <span className="italic">{typingLabel}</span>
              </div>
            )}
          </div>

          {isStaff && aiStatus && (
            <div className="rounded-sm border border-border bg-card p-5" data-testid="ai-assistant-panel">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-lg font-medium"><Sparkles className="h-4 w-4 text-primary" /> Assistant IA</h2>
                {!aiStatus.enabled ? (
                  <span data-testid="ai-locked-note" className="rounded-sm bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                    Disponible avec l'option IA
                  </span>
                ) : aiStatus.status === "trial" ? (
                  <span data-testid="ai-trial-badge" className="rounded-sm bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-300">
                    Essai gratuit jusqu'au {fmtDateTime(aiStatus.trial_end)}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" data-testid="ai-action-reformuler" className="rounded-sm"
                  disabled={!aiStatus.enabled || !!aiBusy || !reply.trim()}
                  title={!reply.trim() ? "Écrivez d'abord un brouillon de réponse ci-dessous" : undefined}
                  onClick={() => runAi("reformuler", () => api.post(`/tickets/${id}/ai/reformuler`, { text: reply }))}>
                  {aiBusy === "reformuler" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : !aiStatus.enabled ? <Lock className="mr-2 h-3.5 w-3.5" /> : null}
                  Reformuler ma réponse
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid="ai-action-resumer" className="rounded-sm"
                  disabled={!aiStatus.enabled || !!aiBusy}
                  onClick={() => runAi("resumer", () => api.post(`/tickets/${id}/ai/resumer`))}>
                  {aiBusy === "resumer" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : !aiStatus.enabled ? <Lock className="mr-2 h-3.5 w-3.5" /> : null}
                  Résumer le ticket
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid="ai-action-synthese" className="rounded-sm"
                  disabled={!aiStatus.enabled || !!aiBusy}
                  onClick={() => runAi("synthese", () => api.post(`/tickets/${id}/ai/synthese-cloture`))}>
                  {aiBusy === "synthese" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : !aiStatus.enabled ? <Lock className="mr-2 h-3.5 w-3.5" /> : null}
                  Synthèse de clôture
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid="ai-action-suggestion" className="rounded-sm"
                  disabled={!aiStatus.enabled || !!aiBusy}
                  onClick={() => runAi("suggestion", () => api.post(`/tickets/${id}/ai/suggestion`))}>
                  {aiBusy === "suggestion" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : !aiStatus.enabled ? <Lock className="mr-2 h-3.5 w-3.5" /> : null}
                  Suggérer catégorie + priorité
                </Button>
              </div>
              {!aiStatus.enabled && (
                <p className="mt-3 text-xs text-muted-foreground">
                  L'Assistant IA est une option activable par entreprise. Contactez votre administrateur Suivio360 pour l'activer ou démarrer un essai gratuit.
                </p>
              )}
              {aiProposal && (
                <div className="mt-4 rounded-sm border border-primary/30 bg-accent/40 p-4" data-testid="ai-proposal-box">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{AI_PROPOSAL_TITLES[aiProposal.type]}</span>
                    <button type="button" data-testid="ai-proposal-dismiss" onClick={() => setAiProposal(null)}
                      className="text-muted-foreground hover:text-foreground transition-colors" title="Fermer la proposition">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {aiProposal.type === "suggestion" ? (
                    <div className="space-y-1 text-sm" data-testid="ai-suggestion-result">
                      <div>Catégorie proposée : <b>{aiProposal.categorie || "Aucune correspondance"}</b></div>
                      <div>Priorité proposée : <b>{AI_PRIO_LABELS[aiProposal.priorite] || "—"}</b></div>
                      {aiProposal.motif && <div className="text-muted-foreground">{aiProposal.motif}</div>}
                      <p className="pt-2 text-xs text-muted-foreground">Suggestion à valider et appliquer manuellement — l'IA ne modifie jamais le ticket.</p>
                    </div>
                  ) : (
                    <>
                      <p className="whitespace-pre-wrap text-sm" data-testid="ai-proposal-text">{aiProposal.text}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {aiProposal.type === "reformulation" && (
                          <Button type="button" size="sm" data-testid="ai-proposal-apply" className="rounded-sm bg-primary text-white hover:bg-red-700"
                            onClick={() => { setReply(aiProposal.text); setAiApplied(true); setAiProposal(null); toast.success("Brouillon remplacé — relisez avant envoi."); }}>
                            Utiliser cette version
                          </Button>
                        )}
                        {aiProposal.type === "synthese" && (
                          <Button type="button" size="sm" data-testid="ai-proposal-apply" className="rounded-sm bg-primary text-white hover:bg-red-700"
                            onClick={() => { setCloseComment(aiProposal.text); setAiProposal(null); setCloseOpen(true); }}>
                            Utiliser comme commentaire de clôture
                          </Button>
                        )}
                        {aiProposal.type === "resume" && (
                          <span className="text-xs text-muted-foreground">Résumé enregistré sur la fiche du ticket.</span>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">Proposition IA — à relire et valider avant utilisation. L'IA n'envoie ni ne clôture jamais automatiquement.</p>
                    </>
                  )}
                </div>
              )}
              {!aiProposal && ticket.ai_summary && (
                <div className="mt-4 rounded-sm border border-border bg-muted/50 p-4" data-testid="ai-last-summary">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Dernier résumé IA — {fmtDateTime(ticket.ai_summary_at)}
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{ticket.ai_summary}</p>
                </div>
              )}
            </div>
          )}

          <form onSubmit={sendReply} className="rounded-sm border border-border bg-card p-5" data-testid="reply-form">
            {!isStaff && ["cloture", "archive"].includes(ticket.status) ? (
              <div data-testid="reply-locked" className="rounded-sm border border-dashed border-border bg-muted/50 p-4 text-center text-sm text-muted-foreground">
                <Lock className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
                Ce ticket est clôturé, vous ne pouvez plus y répondre.
              </div>
            ) : (<>
            {aiApplied && (
              <div className="mb-2 flex items-center gap-2" data-testid="ai-applied-badge">
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                  <Sparkles className="h-3 w-3" /> Version reformulée par l'IA
                </span>
                <span className="text-xs text-muted-foreground">Relisez cette version pro avant d'envoyer.</span>
              </div>
            )}
            <Textarea data-testid="reply-input" rows={4} value={reply}
              onChange={(e) => { setReply(e.target.value); if (aiApplied) setAiApplied(false); notifyTyping(); }}
              placeholder={isStaff ? "Répondre au client ou ajouter une note interne…" : "Votre réponse…"}
              className={`rounded-sm transition-colors ${aiApplied ? "border-primary/60 bg-primary/5 text-primary" : ""}`} />
            {files.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <span key={i} className="rounded-sm border border-border bg-muted px-2 py-1 text-xs">{f.name} ({fmtSize(f.size)})</span>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <input ref={fileInput} type="file" multiple hidden accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                  data-testid="file-input"
                  onChange={(e) => pickFiles(e.target.files)} />
                <Button type="button" variant="outline" data-testid="attach-files-button" onClick={() => fileInput.current?.click()} className="rounded-sm">
                  <Paperclip className="mr-2 h-4 w-4" /> Joindre ({files.length}/15)
                </Button>
                {isStaff && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid="internal-note-checkbox" checked={internal} onCheckedChange={(v) => setInternal(!!v)} />
                    Note interne <span className="text-xs text-muted-foreground">(invisible client)</span>
                  </label>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {suggestReformulate && (
                  <span data-testid="ai-reformulate-hint" className="flex items-center gap-1.5 text-xs text-primary">
                    <Sparkles className="h-3.5 w-3.5" /> Voulez-vous reformuler avec l'IA avant d'envoyer ?
                  </span>
                )}
                {isStaff && aiStatus?.enabled && !internal && (
                  <Button type="button" variant="outline" data-testid="send-with-ai-button"
                    disabled={sending || !!aiBusy || !reply.trim()}
                    onClick={reformulateInline}
                    className="rounded-sm border-primary/50 text-primary hover:bg-primary/5">
                    {aiBusy === "reformuler-inline" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    Reformuler avec l'IA
                  </Button>
                )}
                <Button data-testid="send-reply-button" type="submit" disabled={sending || (!reply.trim() && !files.length)}
                  className="rounded-sm bg-primary text-white hover:bg-red-700">
                  {sending ? "Envoi…" : internal ? "Ajouter la note" : "Envoyer"}
                </Button>
              </div>
            </div>
            </>)}
          </form>
        </div>

        <div className="min-w-0 space-y-6">
          <div className="rounded-sm border border-border bg-card p-5">
            <h2 className="mb-3 text-lg font-medium">Détails</h2>
            {isStaff && <MetaRow label="Société">{ticket.company_name}</MetaRow>}
            {isStaff && <MetaRow label="Client">{ticket.client_name}</MetaRow>}
            <MetaRow label="Catégorie">
              {ticket.category_name ? (
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: ticket.category_color || "#666" }} />{ticket.category_name}</span>
              ) : "—"}
            </MetaRow>
            <MetaRow label={isStaff ? "Assigné à" : "Votre interlocuteur"}>
              {ticket.assignee_name || <span className="text-muted-foreground">{isStaff ? "Non assigné" : "En cours d'assignation"}</span>}
              {isStaff && ticket.assignee_absent && (
                <span data-testid="assignee-absent-badge" className="ml-2 inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-300">
                  Absent · Remplacé par {ticket.assignee_substitute_name}
                </span>
              )}
            </MetaRow>
            {isStaff && <MetaRow label="Manager">{ticket.manager_name}</MetaRow>}
            <MetaRow label="Dernière réponse">{fmtDateTime(ticket.last_message_at)}</MetaRow>
            {ticket.closed_at && <MetaRow label="Clôture">{fmtDateTime(ticket.closed_at)}</MetaRow>}
          </div>

          <div className="rounded-sm border border-border bg-card p-5" data-testid="ticket-files-block">
            <h2 className="mb-4 text-lg font-medium">Fichiers du ticket ({ticket.files?.length || 0})</h2>
            {images.length > 0 && (
              <div className="mb-4 grid grid-cols-3 gap-2">
                {images.map((f) => (
                  <Thumb key={f.id} file={f} testid={`file-thumb-${f.id}`} className="aspect-square w-full" />
                ))}
              </div>
            )}
            <div className="space-y-2">
              {docs.map((f) => (
                <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" data-testid={`file-doc-${f.id}`}
                  className="flex items-center gap-3 rounded-sm border border-border p-3 hover:bg-accent transition-colors duration-200">
                  <FileText className="h-5 w-5 shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{f.original_name}</div>
                    <div className="text-xs text-muted-foreground">{f.ext?.toUpperCase()} · {fmtSize(f.size)} · {f.uploaded_by_name}</div>
                  </div>
                  <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
                </a>
              ))}
            </div>
            {!(ticket.files || []).length && <div className="text-sm text-muted-foreground">Aucun fichier.</div>}
          </div>

          {isStaff && ticket.emails?.length > 0 && (
            <div className="rounded-sm border border-border bg-card p-5" data-testid="ticket-emails-block">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-medium"><Mail className="h-4 w-4" /> Emails liés</h2>
              <div className="space-y-2">
                {ticket.emails.map((e) => (
                  <div key={e.id} className="rounded-sm border border-border p-3 text-xs" data-testid={`ticket-email-${e.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{e.template}</span>
                      <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-300">SIMULÉ</span>
                    </div>
                    <div className="mt-1 text-muted-foreground">→ {e.recipient_email}</div>
                    <div className="truncate text-muted-foreground">{e.subject}</div>
                    <div className="mt-0.5 text-muted-foreground">{fmtDateTime(e.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="rounded-sm" data-testid="close-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Clôturer le ticket {ticket.number} ?</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir clôturer ce ticket ? Le client sera informé que la demande est clôturée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Commentaire de clôture (optionnel)</Label>
            <Textarea data-testid="close-comment-input" rows={3} value={closeComment} onChange={(e) => setCloseComment(e.target.value)}
              className="rounded-sm" placeholder="Ex : Bonjour, l'intervention a été effectuée ce jour. Nous clôturons votre demande." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)} className="rounded-sm">Annuler</Button>
            <Button data-testid="close-ticket-confirm" onClick={closeTicket} className="rounded-sm bg-primary text-white hover:bg-red-700">
              <Lock className="mr-2 h-4 w-4" /> Clôturer le ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
